import {
  takeLatest,
  put,
  call,
  delay,
  select,
  fork,
  all,
} from "redux-saga/effects";
import { actions } from "../util/actions";
import API, { ensureFreshToken } from "../util/api";
import { OTP_METHODS } from "../util/actions";
import { ApiError } from "../util/error";
import { store } from "../redux";
import { showErrorModal, timeout } from "../util/general";
import { getDeviceInfo } from "../util/getDeviceInformation";
import { useNavigate } from "react-router-dom";
import {
  getClassLinkGrantType,
  getInternalSSOClientId,
  getInternalSSOClientSecret,
} from "../util/classLinkSSOConfig";
import { normalizeTeacherImplementations } from "../util/teacherImplementations";

const SSO_AUTH_CLIENT_ID = import.meta.env.VITE_SSO_AUTH_CLIENT_ID || "";
const SSO_AUTH_CLIENT_SECRET =
  import.meta.env.VITE_SSO_AUTH_CLIENT_SECRET || "";
const AUTH_CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID || "";
const AUTH_CLIENT_SECRET = import.meta.env.VITE_AUTH_CLIENT_SECRET || "";
const DEFAULT_AUTH_SCOPE =
  import.meta.env.VITE_DEFAULT_AUTH_SCOPE || "isafe_direct";

const isAltLoginRoute = () => {
  if (typeof window === "undefined") return false;
  const path = window.location?.pathname || "";
  return path.startsWith("/user/login/alt");
};

// Get the expiry time
function calculateExpiryTime(expiresIn) {
  const buffer = 60 * 1000;
  return Date.now() + expiresIn * 1000 - buffer;
}

const processILEARNData = (ilearnChapters = [], userIlearn = []) => {
  const processedData = {};

  // Map user progress to easily look up by module_no and chapter_no
  const userProgressMap =
    Array.isArray(userIlearn) && userIlearn?.length > 0
      ? userIlearn?.reduce((acc, module, moduleIndex) => {
          if (Array.isArray(module)) {
            acc[moduleIndex] = module.reduce((chapterAcc, chapter) => {
              const key = `${chapter.chapter_no}`;
              chapterAcc[key] = {
                is_completed: 1,
                watch_date: chapter.watch_date,
              };
              return chapterAcc;
            }, {});
          }
          return acc;
        }, {})
      : {};

  // Process chapters and merge with user progress
  if (Array.isArray(ilearnChapters)) {
    ilearnChapters.forEach((module, moduleIndex) => {
      const moduleNumber = moduleIndex.toString();
      const userModuleData = userProgressMap[moduleIndex] || {};

      // Initialize module data
      processedData[moduleNumber] = {
        chapters: [],
        totalChapters: module.length || 0,
        completedChapters: 0,
        is_completed: false, // Default to false
      };

      // Process each chapter
      module.forEach((chapter) => {
        // Check user progress for the chapter
        const userChapterData = userModuleData[chapter.chapter_no] || {
          is_completed: 0,
          watch_date: null,
        };

        const chapterData = {
          ...chapter,
          ...userChapterData, // Merge progress data
        };

        // Increment completed chapters count if chapter is marked as completed
        if (userChapterData.is_completed) {
          processedData[moduleNumber].completedChapters += 1;
        }

        processedData[moduleNumber].chapters.push(chapterData);
      });

      // Update module's is_completed if all chapters are completed
      if (
        processedData[moduleNumber].completedChapters ===
        processedData[moduleNumber].totalChapters
      ) {
        processedData[moduleNumber].is_completed = true;
      }
    });
  }

  return processedData;
};

// Retry helper for API calls in sagas
function* callWithRetry(apiFn, args = [], retries = 1, delayMs = 500) {
  let attempt = 0;
  while (true) {
    try {
      return yield call(apiFn, ...args);
    } catch (error) {
      if (attempt >= retries) {
        throw error;
      }
      attempt += 1;
      yield delay(delayMs);
    }
  }
}

function* loggedApiCall(label, apiFn, ...args) {
  const startTime = Date.now();
  console.log(
    `[LoginTrace] ${label} - calling api at ${new Date(
      startTime,
    ).toISOString()}`,
  );
  try {
    const response = yield call(apiFn, ...args);
    const endTime = Date.now();
    console.log(
      `[LoginTrace] ${label} - received response at ${new Date(
        endTime,
      ).toISOString()} (elapsed ${endTime - startTime}ms)`,
    );
    return response;
  } catch (error) {
    const endTime = Date.now();
    console.error(
      `[LoginTrace] ${label} - failed at ${new Date(
        endTime,
      ).toISOString()} (elapsed ${endTime - startTime}ms)`,
      error,
    );
    throw error;
  }
}

const normalizeId = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

let preservedSubscriptionContext = null;
let subscriptionDetailSagaSeq = 0;

const hasSelectionIdentity = (selection = {}) =>
  Boolean(
    normalizeId(selection?.invoice_id) &&
    (normalizeId(selection?.customer_id) ||
      normalizeId(selection?.customer_code)),
  );

const resolveUserId = (value) => {
  const visited = new Set();
  let current = value;

  while (current && typeof current === "object") {
    if (visited.has(current)) {
      break;
    }
    visited.add(current);
    if ("user_id" in current) {
      current = current.user_id;
    } else {
      break;
    }
  }

  if (current === undefined || current === null) {
    return null;
  }
  return String(current);
};

const sanitizeSchoolList = (list = []) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).reduce((acc, raw) => {
    const orgId = normalizeId(raw?.org_id ?? raw?.school_id ?? raw?.orgId);
    if (!orgId || seen.has(orgId)) {
      return acc;
    }
    seen.add(orgId);
    acc.push({
      ...raw,
      org_id: orgId,
    });
    return acc;
  }, []);
};

const sanitizeSchoolLevels = (list = []) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).reduce((acc, raw) => {
    const code = normalizeId(raw?.school_level);
    if (!code || seen.has(code)) {
      return acc;
    }
    seen.add(code);
    acc.push({
      ...raw,
      school_level: code,
      school_level_desc:
        typeof raw?.school_level_desc === "string"
          ? raw.school_level_desc.trim()
          : (raw?.school_level_desc ?? code),
    });
    return acc;
  }, []);
};

function* loadTeacherImplementations(teacherId, customerId) {
  if (!teacherId || !customerId) {
    yield put({
      type: actions.STORE_TEACHER_IMPLEMENTATIONS,
      implementations: [],
      fetchedAt: Date.now(),
    });
    yield put({
      type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
      loading: false,
    });
    return;
  }

  yield put({
    type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
    loading: true,
  });

  const startTime = Date.now();
  console.log(
    `[TeacherImplementations] Timestamp before calling implementation api: ${new Date(
      startTime,
    ).toISOString()}`,
  );
  const logContentLoaded = () => {
    const loadedAt = Date.now();
    console.log(
      `[TeacherImplementations] Timestamp content loaded: ${new Date(
        loadedAt,
      ).toISOString()} (total elapsed ${loadedAt - startTime}ms)`,
    );
  };

  yield put({
    type: actions.SET_TEACHER_IMPLEMENTATIONS_FETCHED_AT,
    fetchedAt: Date.now(),
  });

  try {
    const teacherImplResp = yield call(
      callWithRetry,
      API.getTeacherImplementationsNew,
      [{ customer_id: customerId, teacher_id: teacherId }],
    );
    console.log(
      `[TeacherImplementations] Timestamp after api response: ${new Date(
        Date.now(),
      ).toISOString()}`,
    );
    if (teacherImplResp?.status === "Success") {
      const details = Array.isArray(teacherImplResp.detail)
        ? teacherImplResp.detail
        : [];
      const normalizedImplementations = normalizeTeacherImplementations(
        details,
        teacherId,
      );
      yield put({
        type: actions.STORE_TEACHER_IMPLEMENTATIONS,
        implementations: normalizedImplementations,
        fetchedAt: Date.now(),
      });
      logContentLoaded();
    } else {
      yield put({
        type: actions.STORE_TEACHER_IMPLEMENTATIONS,
        implementations: [],
        fetchedAt: Date.now(),
      });
      logContentLoaded();
    }
  } catch (error) {
    console.error("Failed to load teacher implementations:", error);
    yield put({
      type: actions.STORE_TEACHER_IMPLEMENTATIONS,
      implementations: [],
      fetchedAt: Date.now(),
    });
    logContentLoaded();
  } finally {
    yield put({
      type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
      loading: false,
    });
  }
}

function* fetchUserProfileAfterLogin() {
  try {
    const userProfile = yield call(
      loggedApiCall,
      "Fetch User Profile",
      callWithRetry,
      API.getUserProfile,
    );
    yield put({
      type: actions.STORE_PROFILE_INFO,
      userProfile,
    });
    return userProfile;
  } catch (error) {
    console.error("Failed to load user profile after login:", error);
    return null;
  }
}

const COUNT_USER_LOGIN_DELAY_MS = 1000;

function* fetchTeacherImplementationsAfterLogin() {
  let teacherImplResp = null;
  yield put({
    type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
    loading: true,
  });
  try {
    teacherImplResp = yield call(
      loggedApiCall,
      "Fetch Teacher Implementations (Token)",
      callWithRetry,
      API.getTeacherImplementationsNew,
      [{}],
    );

    const responseUserId = Array.isArray(teacherImplResp?.detail)
      ? (teacherImplResp.detail.find((item) => item?.user_id)?.user_id ?? null)
      : null;
    const stateUserId = yield select(
      (state) =>
        state?.auth?.user?.user_id ??
        state?.auth?.loginResponse?.user_id ??
        null,
    );
    const fallbackTeacherId = responseUserId ?? stateUserId ?? null;

    if (teacherImplResp?.status === "Success") {
      const details = Array.isArray(teacherImplResp.detail)
        ? teacherImplResp.detail
        : [];
      const normalizedImplementations = normalizeTeacherImplementations(
        details,
        fallbackTeacherId,
      );
      yield put({
        type: actions.STORE_TEACHER_IMPLEMENTATIONS,
        implementations: normalizedImplementations,
        fetchedAt: Date.now(),
      });
    } else {
      yield put({
        type: actions.STORE_TEACHER_IMPLEMENTATIONS,
        implementations: [],
        fetchedAt: Date.now(),
      });
    }
  } catch (error) {
    console.error("Failed to load teacher implementations after login:", error);
    yield put({
      type: actions.STORE_TEACHER_IMPLEMENTATIONS,
      implementations: [],
      fetchedAt: Date.now(),
    });
  } finally {
    yield put({
      type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
      loading: false,
    });
    yield delay(COUNT_USER_LOGIN_DELAY_MS);
    try {
      yield call(
        loggedApiCall,
        "Count User Login",
        callWithRetry,
        API.countUserLogin,
        [{}],
      );
    } catch (countError) {
      console.error(
        "Failed to count user login after implementations load:",
        countError,
      );
    }
  }
  return teacherImplResp;
}

function* preloadSubscriptionSchools(teacherSubscriptions = []) {
  const uniqueOrgStates = Array.from(
    new Set(
      (teacherSubscriptions || [])
        .map((sub) => sub?.org_state || sub?.state || null)
        .filter(Boolean),
    ),
  );

  if (!uniqueOrgStates.length) {
    yield put({
      type: actions.STORE_SUBSCRIPTION_SCHOOLS,
      schoolsByState: {},
    });
    return;
  }

  let schoolsByState = {};
  try {
    const schoolResponses = yield all(
      uniqueOrgStates.map((stateCode) =>
        call(callWithRetry, API.getSubscriptionSchools, [{ state: stateCode }]),
      ),
    );

    schoolsByState = schoolResponses.reduce((acc, resp, idx) => {
      const stateCode = uniqueOrgStates[idx];
      acc[stateCode] = sanitizeSchoolList(resp?.schools);
      return acc;
    }, {});
  } catch (schoolError) {
    console.error("Failed to preload subscription schools:", schoolError);
  }

  yield put({
    type: actions.STORE_SUBSCRIPTION_SCHOOLS,
    schoolsByState,
  });
}

function* preloadSchoolLevels() {
  try {
    const schoolLevelsResp = yield call(callWithRetry, API.getSchoolLevels);
    const schoolLevels = sanitizeSchoolLevels(schoolLevelsResp?.school_levels);
    yield put({
      type: actions.STORE_SCHOOL_LEVELS,
      schoolLevels,
    });
  } catch (levelsError) {
    console.error("Failed to preload school levels:", levelsError);
    yield put({
      type: actions.STORE_SCHOOL_LEVELS,
      schoolLevels: [],
    });
  }
}

// Utility functions (Geolocation and Device Info)
const getLocation = () => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: position.timestamp,
        });
      },
      (error) => {
        reject(error);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    );
  });
};

const extractCustomerId = (rawCustomer = null) => {
  if (!rawCustomer) return null;
  if (typeof rawCustomer === "string" || typeof rawCustomer === "number") {
    return rawCustomer;
  }
  if (typeof rawCustomer === "object") {
    const firstKey = Object.keys(rawCustomer)[0];
    if (!firstKey) return null;
    const entry = rawCustomer[firstKey];
    if (!entry) return null;
    if (typeof entry === "object") {
      return entry.customer_id ?? null;
    }
    if (typeof entry === "string" || typeof entry === "number") {
      return entry;
    }
  }
  return null;
};

const sendUserDeviceLocation = async (deviceId) => {
  try {
    const { latitude, longitude, timestamp } = await getLocation();
    const user_id = store.getState().auth.user.user_id;
    const networkState =
      (await navigator.connection) ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    const ipAddress = networkState ? networkState.effectiveType : null;
    await timeout(3000);
    const response = await API.updateUserDeviceLocation(
      user_id,
      deviceId,
      ipAddress,
      longitude,
      latitude,
      timestamp,
    );
  } catch (error) {
    console.error(error);
  }
};

const get_location = async (device_id) => {
  try {
    const granted = true; // For web, assume permission is granted
    if (granted) {
      await sendUserDeviceLocation(device_id);
    }
  } catch (error) {
    console.error(error);
  }
};

// Sagas
function* userLoginAsync(action) {
  const { loginResponse } = action;
  try {
    if (loginResponse.access_token) {
      // Calculate expiry time and update loginResponse with it
      const expiryTime = calculateExpiryTime(loginResponse.expires_in);
      const updatedLoginResponse = {
        ...loginResponse,
        expiryTime,
      };

      yield put({
        type: actions.USER_LOGIN,
        loginResponse: updatedLoginResponse,
      });

      yield put({
        type: actions.SET_TEACHER_IMPLEMENTATIONS_LOADING,
        loading: true,
      });

      yield put({
        type: actions.SET_TEACHER_IMPLEMENTATIONS_FETCHED_AT,
        fetchedAt: Date.now(),
      });

      yield fork(fetchUserProfileAfterLogin);
      yield fork(fetchTeacherImplementationsAfterLogin);
    } else {
      console.log("Invalid response structure from server");
    }
  } catch (error) {
    console.error("Error in saga/auth.js", error);
  }
}

function* fetchResourceDownloads() {
  const alreadyLoaded = yield select((state) => state.auth.resources_loaded);
  if (alreadyLoaded) {
    return;
  }
  try {
    const [getSubscriptionVideos, getAnnouncements, getCurriculum] = yield all([
      call(callWithRetry, API.getSubscriptionVideos),
      call(callWithRetry, API.getAnnouncements),
      call(callWithRetry, API.getCurriculum),
    ]);

    yield put({
      type: actions.STORE_RESOURCE_DOWNLOAD,
      getSubscriptionVideos,
      getAnnouncements,
      getCurriculum,
    });
  } catch (error) {
    console.error("Failed to load subscription resources:", error);
  }
}

function* fetchIlearnContent() {
  const alreadyLoaded = yield select((state) => state.ilearn?.loaded);
  if (alreadyLoaded) {
    return;
  }
  const userId = yield select((state) => state.auth.user?.user_id ?? null);
  const resolvedUserId = resolveUserId(userId);
  try {
    const [getILEARNModules, getILEARNChapters, getILEARNUser] = yield all([
      call(callWithRetry, API.getILEARNModules),
      call(callWithRetry, API.getILEARNChapters),
      call(callWithRetry, API.getILEARNUser, [{ user_id: resolvedUserId }]),
    ]);

    yield put({
      type: actions.STORE_ILEARN_USER_INFO,
      getILEARNUser,
    });

    const chapters = processILEARNData(
      getILEARNChapters?.ilearn_chapters,
      getILEARNUser?.user_ilearn,
    );

    yield put({
      type: actions.STORE_MODULE_INFO,
      getILEARNModules,
      chapters,
    });
  } catch (error) {
    console.error("Failed to load ILEARN content:", error);
  }
}

function* googleSSOLoginAction(action) {
  const { id_token, email, captcha_token, honeypotPayload } = action;
  try {
    if (!id_token) {
      yield call(
        showErrorModal,
        "Google sign-in could not be completed. Please try again.",
      );
      return;
    }

    const payload = {
      google_id_token: id_token,
      email,
      client_id: AUTH_CLIENT_ID,
      grant_type: "google_sso",
      authenticator: "google",
      client_secret: AUTH_CLIENT_SECRET,
      captcha_token,
      // Do NOT send a fixed scope — the backend will derive the correct scopes
      // from the user's actual Drupal roles (same fallback as PasswordGrant).
      // Sending "isafe_direct" here caused 403s because that value is not a
      // real Drupal role and TokenAuthUser ended up with no valid permissions.
    };

    // Only send honeypot field if it has a value, using the expected key name
    if (honeypotPayload) {
      payload.website_url = honeypotPayload;
    }

    const googleSSOResponse = yield call(
      loggedApiCall,
      "Google SSO Token",
      API.googleSSO,
      payload,
    );
    if (googleSSOResponse.access_token != null) {
      yield put({
        type: actions.SET_SSO_FLAG,
        is_sso: 1,
      });
      yield call(userLoginAsync, { loginResponse: googleSSOResponse });
    } else {
      yield call(
        showErrorModal,
        "Oops! It looks like you can't log in with Google right now. Please make sure you have an active ISAFE subscription to proceed.",
      );
    }
  } catch (error) {
    console.error("Error during Google SSO:", error);
  }
}

function* classLinkSSOLoginAction(action) {
  const { code, redirectUri, pixelToken } = action;
  try {
    const payload = {
      code,
      client_id: SSO_AUTH_CLIENT_ID,
      grant_type: "google_sso",
      client_secret: SSO_AUTH_CLIENT_SECRET,
      is_classlink_sso: 1,
      scope: DEFAULT_AUTH_SCOPE,
    };

    if (pixelToken) {
      payload.captcha_token = pixelToken;
    }

    const response = yield call(
      loggedApiCall,
      "ClassLink SSO Token",
      API.classLinkSSO,
      payload,
    );

    if (response?.access_token) {
      yield put({
        type: actions.SET_SSO_FLAG,
        is_sso: 1,
      });
      yield call(userLoginAsync, { loginResponse: response });
    } else {
      const message =
        response?.hint ||
        "Oops! It looks like you can't log in with ClassLink right now. Please make sure you have an active ISAFE subscription to proceed.";
      yield call(showErrorModal, message);
    }
  } catch (error) {
    console.error("Error during ClassLink SSO:", error);
    const message =
      "An unexpected error occurred while finishing ClassLink sign-in. Please try again.";
    yield call(showErrorModal, message);
  }
}

function* microsoftSSOLoginAction(action) {
  const { idToken: incomingIdToken, pixelToken, code } = action;
  const idToken = incomingIdToken || code;
  try {
    if (!idToken) {
      throw new Error("missing_id_token");
    }

    const payload = {
      microsoft_id_token: idToken,
      client_id: SSO_AUTH_CLIENT_ID,
      grant_type: "google_sso",
      client_secret: SSO_AUTH_CLIENT_SECRET,
      is_microsoft_sso: 1,
      scope: DEFAULT_AUTH_SCOPE,
    };

    if (pixelToken) {
      payload.captcha_token = pixelToken;
    }

    const response = yield call(
      loggedApiCall,
      "Microsoft SSO Token",
      API.microsoftSSO,
      payload,
    );

    if (response?.access_token) {
      yield put({
        type: actions.SET_SSO_FLAG,
        is_sso: 1,
      });
      yield call(userLoginAsync, { loginResponse: response });
    } else {
      const message =
        response?.hint ||
        "Oops! It looks like you can't log in with Microsoft right now. Please make sure you have an active ISAFE subscription to proceed.";
      yield call(showErrorModal, message);
    }
  } catch (error) {
    console.error("Error during Microsoft SSO:", error);
    const message =
      error?.message === "missing_id_token"
        ? "We could not verify your Microsoft account. Please try again."
        : "An unexpected error occurred while finishing Microsoft sign-in. Please try again.";
    yield call(showErrorModal, message);
  }
}

function* isafeDirectSSOLoginAction(action) {
  const { accessToken, email } = action;
  try {
    yield put({ type: actions.ISAFE_DIRECT_SSO_START });
    if (!accessToken) {
      throw new Error("missing_access_token");
    }

    const payload = {
      client_id: SSO_AUTH_CLIENT_ID,
      grant_type: "google_sso",
      client_secret: SSO_AUTH_CLIENT_SECRET,
      exchange_token: accessToken,
      is_token_exchange: "1",
      scope: DEFAULT_AUTH_SCOPE,
    };
    if (email) {
      payload.email = email;
    }

    const response = yield call(
      loggedApiCall,
      "ISAFE Direct SSO Token",
      API.ssoLogin,
      payload,
    );

    if (response?.access_token) {
      yield put({
        type: actions.SET_SSO_FLAG,
        is_sso: 1,
      });
      yield put({ type: actions.ISAFE_DIRECT_SSO_SUCCESS });
      yield call(userLoginAsync, { loginResponse: response });
    } else {
      const message =
        response?.hint ||
        "Oops! It looks like you can't log in with ISAFE Direct right now. Please make sure you have an active ISAFE subscription to proceed.";
      yield put({ type: actions.ISAFE_DIRECT_SSO_FAILURE, error: message });
    }
  } catch (error) {
    console.error("Error during ISAFE Direct SSO:", error);
    const message =
      error?.message === "missing_access_token"
        ? "We could not find your ISAFE Direct access token. Please try again."
        : "An unexpected error occurred while finishing ISAFE Direct sign-in. Please try again.";
    yield put({ type: actions.ISAFE_DIRECT_SSO_FAILURE, error: message });
  }
}

function* becomeUser(action) {
  const { user_id } = action;
  try {
    const authState = yield select((state) => state.auth || {});
    const origin = authState?.become_origin;
    const selectedSubscription = authState?.selected_subscriptions || {};
    const subscriptionDetails = authState?.subscription_details || {};
    const hadSelection = hasSelectionIdentity(selectedSubscription);

    if (origin !== "directory" && hadSelection) {
      preservedSubscriptionContext = {
        selected_subscription: { ...selectedSubscription },
        subscription_details: subscriptionDetails,
      };
    }

    yield put({
      type: actions.SHOW_GLOBAL_LOADING,
      payload: { message: "Zooming into user..." },
    });
    // Fetch and store user profile
    const userProfile = yield call(API.getTeacherProfile, { user_id });
    console.log("User Profile", userProfile);
    yield put({
      type: actions.STORE_PROFILE_INFO,
      userProfile,
    });

    const getTeacherSubscriptions = yield call(API.getTeacherSubscriptions, {
      user_id,
    });
    const teacherSubscriptions = Array.isArray(
      getTeacherSubscriptions?.subscription,
    )
      ? getTeacherSubscriptions.subscription
      : [];

    yield put({
      type: actions.STORE_TEACHER_SUBSCRIPTIONS,
      getTeacherSubscriptions,
    });

    const resolvedTeacherId = resolveUserId(
      userProfile?.user?.user_id ?? user_id,
    );
    const resolvedCustomerId = extractCustomerId(
      userProfile?.user?.customer_id,
    );

    yield put({
      type: actions.STORE_TEACHER_IMPLEMENTATIONS,
      implementations: [],
      fetchedAt: Date.now(),
    });
    yield call(
      loadTeacherImplementations,
      resolvedTeacherId,
      resolvedCustomerId,
    );

    yield call(preloadSubscriptionSchools, teacherSubscriptions);
    yield call(preloadSchoolLevels);
    yield put({
      type: actions.SET_BECOME_USER,
      become_user: 1,
    });
    yield put({
      type: actions.SET_BECOME,
      become: 0,
    });

    // Keep subscription dropdown/data available while impersonating.
    if (origin !== "directory" && hadSelection) {
      const invoiceId = selectedSubscription?.invoice_id;
      const customerId = selectedSubscription?.customer_id;
      const customerCode = selectedSubscription?.customer_code;
      const hasInvoiceDetail =
        Array.isArray(subscriptionDetails?.invoiceDetail) &&
        subscriptionDetails.invoiceDetail.length > 0;

      if (!hasInvoiceDetail && invoiceId && (customerId || customerCode)) {
        try {
          const resp = yield call(API.getSubscriptionDetail, {
            invoice_id: invoiceId,
            customer_id: customerId,
            customer_code: customerCode,
          });
          const {
            message,
            status,
            quotes = [],
            certify_implementation,
            customer_detail,
            invoice_detail = [],
          } = resp || {};
          const normalized = {
            status,
            message,
            quotes,
            certifyImplementation: certify_implementation === "1",
            customerDetail: customer_detail,
            invoiceDetail: invoice_detail,
          };
          yield put({
            type: actions.PUT_SUBSCRIPTION_DETAIL,
            subscription_details: normalized,
          });
        } catch (subscriptionDetailError) {
          console.error(
            "Failed to load subscription details during becomeUser:",
            subscriptionDetailError,
          );
        }
      }
    }

    // const getSubscriptionVideos = yield call(API.getSubscriptionVideos);
    // const getAnnouncements = yield call(API.getAnnouncements);
    // const getCurriculum = yield call(API.getCurriculum);
    // yield put({
    //   type: actions.STORE_RESOURCE_DOWNLOAD,
    //   getSubscriptionVideos,
    //   getAnnouncements,
    //   getCurriculum,
    // });

    const getILEARNModules = yield call(API.getILEARNModules);
    const getILEARNChapters = yield call(API.getILEARNChapters);
    const getILEARNUser = yield call(API.getILEARNUser, { user_id });

    yield put({
      type: actions.STORE_ILEARN_USER_INFO,
      getILEARNUser,
    });
    const chapters = processILEARNData(
      getILEARNChapters?.ilearn_chapters,
      getILEARNUser?.user_ilearn,
    );

    yield put({
      type: actions.STORE_MODULE_INFO,
      getILEARNModules,
      chapters,
    });
  } catch (error) {
    console.error("Error in saga/auth.js/becomeUser", error);
  } finally {
    yield put({ type: actions.HIDE_GLOBAL_LOADING });
  }
}

function* unbecomeUser(action) {
  try {
    yield put({
      type: actions.SHOW_GLOBAL_LOADING,
      payload: { message: "Zooming out user..." },
    });
    // Fetch and store user profile
    const userProfile = yield call(API.getUserProfile);
    console.log("User Profile", userProfile);
    yield put({
      type: actions.STORE_PROFILE_INFO,
      userProfile,
    });

    // Determine who is unbecoming based on roles
    const roles = userProfile?.user?.roles || [];
    const origin = yield select((state) => state.auth.become_origin);
    const roleIDs = new Set(roles?.map((r) => r.id));
    const ISAFE_ROLES = new Set([
      "isafe_accountant",
      "isafe_moderator",
      "isafe_salesperson",
      "isafe_admin",
      "root",
    ]);
    const SUBSCRIPTION_ROLES = new Set([
      "subscription_access",
      "subscription_accountant",
      "subscription_admin",
      "subscription_implementor",
      "subscription_salesperson",
    ]);

    const isISAFEAdmin = Array.from(ISAFE_ROLES).some((r) => roleIDs.has(r));
    const isSubscriptionAdmin = Array.from(SUBSCRIPTION_ROLES).some((r) =>
      roleIDs.has(r),
    );

    // Apply requested behavior:
    // - If flow started from directory, return to ISAFE-level listing (become = 0)
    // - Otherwise, ISAFE admins should return to subscription zoom-in mode (become = 1)
    // - Non-ISAFE subscription admins remain at ISAFE-level view (become = 0)
    const becomeValue =
      origin === "directory"
        ? 0
        : isISAFEAdmin
          ? 1
          : isSubscriptionAdmin
            ? 0
            : 0;

    yield put({
      type: actions.SET_BECOME_USER,
      become_user: 0,
    });
    yield put({
      type: actions.SET_BECOME,
      become: becomeValue,
    });

    yield put({
      type: actions.SET_BECOME_ORIGIN,
      origin: null,
    });

    if (
      becomeValue === 1 &&
      preservedSubscriptionContext?.selected_subscription &&
      hasSelectionIdentity(preservedSubscriptionContext.selected_subscription)
    ) {
      yield put({
        type: actions.STORE_SELECTED_SUBSCRIPTION,
        selected_subscription:
          preservedSubscriptionContext.selected_subscription,
      });
      if (preservedSubscriptionContext.subscription_details) {
        yield put({
          type: actions.PUT_SUBSCRIPTION_DETAIL,
          subscription_details:
            preservedSubscriptionContext.subscription_details,
        });
      }
    }

    const getSubscriptionVideos = yield call(API.getSubscriptionVideos);
    const getAnnouncements = yield call(API.getAnnouncements);
    const getCurriculum = yield call(API.getCurriculum);
    yield put({
      type: actions.STORE_RESOURCE_DOWNLOAD,
      getSubscriptionVideos,
      getAnnouncements,
      getCurriculum,
    });

    const getILEARNModules = yield call(API.getILEARNModules);
    const getILEARNChapters = yield call(API.getILEARNChapters);
    const userId = resolveUserId(userProfile?.user?.user_id);
    const getILEARNUser = yield call(API.getILEARNUser, { user_id: userId });

    yield put({
      type: actions.STORE_ILEARN_USER_INFO,
      getILEARNUser,
    });
    const chapters = processILEARNData(
      getILEARNChapters?.ilearn_chapters,
      getILEARNUser?.user_ilearn,
    );

    yield put({
      type: actions.STORE_MODULE_INFO,
      getILEARNModules,
      chapters,
    });
  } catch (error) {
    console.error("Error in saga/auth.js/becomeUser", error);
  } finally {
    yield put({ type: actions.HIDE_GLOBAL_LOADING });
  }
}

function* updateSelfImplementation(action) {
  const { user_id, value } = action;

  try {
    const response = yield call(API.selfImplementation, {
      user_id,
      self_implemented: value,
    });

    const responseStatus = response?.status;
    if (responseStatus && String(responseStatus).toLowerCase() !== "success") {
      throw new Error(
        response?.message || "Unable to update self implementation.",
      );
    }

    yield put({
      type: actions.SET_SELF_IMPLEMENTED,
      value,
    });

    yield put({
      type: actions.SELF_IMPLEMENTATION_SUCCESS,
      notice: {
        status: value === 1 ? "set" : "unset",
        timestamp: Date.now(),
      },
    });
  } catch (error) {
    console.error("Error updating self implementation:", error);
    const message =
      error?.message ||
      "Unable to update self implementation. Please try again.";

    yield put({
      type: actions.SELF_IMPLEMENTATION_FAILURE,
      notice: {
        status: "error",
        message,
        timestamp: Date.now(),
      },
    });

    yield call(showErrorModal, message);
  }
}

function* logoutUserAsync(action) {
  // No-op: anonymous users do not require a token; skip /oauth/token call on logout.
}

function* refreshTokenSaga() {
  // Allow up to 3 consecutive failures (e.g. transient network error, or a
  // race-condition loss against ensureFreshToken()) before forcing logout.
  const MAX_REFRESH_FAILURES = 3;
  const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // fallback poll interval
  const ANON_RETRY_DELAY_MS = 5000;
  const REFRESH_BUFFER_MS = 60 * 1000; // refresh 1 minute before expiry

  let refreshFailures = 0;

  try {
    while (true) {
      const loginResponse = yield select((state) => state.auth.loginResponse);
      const accessToken = loginResponse?.access_token;
      const isSSOLogin = yield select((state) => state.auth.is_sso);

      if (!accessToken || isSSOLogin === 1) {
        // No token or SSO user – check again after fallback interval
        yield delay(REFRESH_INTERVAL_MS);
        continue;
      }

      // If we know when the token expires, wait until just before expiry
      const expiryTime = loginResponse?.expiryTime;
      if (expiryTime) {
        const msUntilRefresh = expiryTime - Date.now() - REFRESH_BUFFER_MS;
        if (msUntilRefresh > 0) {
          // Token still valid – sleep until it's about to expire
          yield delay(Math.min(msUntilRefresh, REFRESH_INTERVAL_MS));
          continue;
        }
      }

      // Token is expired / about to expire (or no expiryTime) – refresh now.
      // Use ensureFreshToken() so this call shares the same _refreshInFlight
      // lock as the per-request pre-flight and 401 retry paths. This prevents
      // two callers from consuming the same rotating refresh token simultaneously.
      // ensureFreshToken() dispatches UPDATE_ACCESS_TOKEN (with expiryTime)
      // internally, so no separate yield put is needed here.
      try {
        yield call(ensureFreshToken);
        refreshFailures = 0;
      } catch (error) {
        console.error("Error refreshing access token:", error);
        refreshFailures += 1;
        if (refreshFailures >= MAX_REFRESH_FAILURES) {
          console.error("Max refresh attempts reached. Logging out user.");
          yield put({ type: actions.LOG_OUT });
          return;
        }

        yield delay(ANON_RETRY_DELAY_MS);
      }
      // Loop immediately so the new expiryTime drives the next sleep duration
    }
  } catch (error) {
    console.error("Error in token refresh saga:", error);
  }
}

function* refreshToken() {
  try {
    const newTokenData = yield call(API.refreshUserToken);
    const currentData = yield select((state) => state.auth.loginResponse);
    // console.log("Current Data: " + JSON.stringify(currentData));
    // console.log("New Data :" + JSON.stringify(newTokenData));
    const expiryTime = calculateExpiryTime(newTokenData.expires_in);

    // Update store with new token and expiry time
    yield put({
      type: actions.USER_LOGIN,
      loginResponse: {
        ...newTokenData,
        expiryTime,
      },
    });
  } catch (error) {
    console.error("Error refreshing token:", error);
    // Handle token refresh failure (e.g., redirect to login)
  }
}

// function* checkTokenExpiry() {
//   while (true) {
//     yield delay(300000);
//     console.log("Checked access token validity");

//     const loginResponse = yield select((state) => state.auth.loginResponse);

//     // Check if loginResponse and access_token are defined
//     if (loginResponse && loginResponse.access_token) {
//       const { expiryTime } = loginResponse;

//       // if (Date.now() >= expiryTime) {
//       //   console.log("Called Refresh Token API");
//       yield call(refreshToken);
//       // }
//     } else {
//       console.log("No access token available. Skipping token check.");
//     }
//   }
// }

function* getAdminRoles(action) {
  try {
    // Call API.getUserToken to get the anonymous token
    const response = yield call(API.getAdminRoles);
    if (response?.roles) {
      // Dispatch an action to store the anonymous token in Redux
      yield put({
        type: "STORE_ADMIN_ROLES",
        roles: response.roles,
      });
      // console.log("Anonymous token stored after logout.");
    } else {
      console.error("Failed to fetch admin roles", response?.message);
    }
  } catch (error) {
    console.error("Error in logoutUserAsync saga:", error);
  }
}

function* getSubscriptionDetails(action) {
  const seq = ++subscriptionDetailSagaSeq;
  try {
    // 1) get anon token if your API requires it
    const { invoice_id, customer_id, customer_code } = action;
    const resp = yield call(API.getSubscriptionDetail, {
      invoice_id,
      customer_id,
      customer_code,
    });

    const {
      message,
      status,
      quotes = [],
      certify_implementation,
      customer_detail,
      invoice_detail = [],
    } = resp;

    const normalized = {
      status,
      message,
      quotes,
      certifyImplementation: certify_implementation === "1",
      customerDetail: customer_detail,
      invoiceDetail: invoice_detail,
    };

    // 4) put it in your store
    yield put({
      type: actions.PUT_SUBSCRIPTION_DETAIL,
      subscription_details: normalized,
    });
  } catch (error) {}
}

// Root saga
export function* authSaga() {
  yield takeLatest(actions.USER_LOGIN_ASYNC, userLoginAsync);
  yield takeLatest(
    actions.FETCH_RESOURCE_DOWNLOAD_ASYNC,
    fetchResourceDownloads,
  );
  yield takeLatest(actions.FETCH_ILEARN_CONTENT_ASYNC, fetchIlearnContent);
  yield takeLatest(actions.GOOGLE_SSO_LOGIN, googleSSOLoginAction);
  yield takeLatest(actions.CLASSLINK_SSO_LOGIN, classLinkSSOLoginAction);
  yield takeLatest(actions.MICROSOFT_SSO_LOGIN, microsoftSSOLoginAction);
  yield takeLatest(actions.ISAFE_DIRECT_SSO_LOGIN, isafeDirectSSOLoginAction);
  yield takeLatest(actions.BECOME_USER, becomeUser);
  yield takeLatest(actions.UNBECOME_USER, unbecomeUser);
  yield takeLatest(actions.SELF_IMPLEMENTATION_ASYNC, updateSelfImplementation);
  yield takeLatest(actions.LOG_OUT, logoutUserAsync);
  yield takeLatest(actions.GET_ADMIN_ROLES, getAdminRoles);
  yield takeLatest(
    actions.GET_SUBSCRIPTION_DETAIL_ASYNC,
    getSubscriptionDetails,
  );
  // yield takeLatest(actions.CHECK_TOKEN_EXPIRY, checkTokenExpiry);
  yield fork(refreshTokenSaga);
  // yield fork(checkTokenExpiry);
}
